// src/pages/WritePost/index.jsx (修复后的完整代码)
import React, { useState, useEffect } from 'react';
import { 
  Layout, 
  Card, 
  Form, 
  Input, 
  Button, 
  message, 
  Select, 
  Tag,
  Space,
  Divider,
  InputNumber,
  Modal
} from 'antd';
import { 
  SaveOutlined, 
  CloseCircleOutlined, 
  EditOutlined,
  PlusOutlined,
  MinusCircleOutlined,
  EyeOutlined
} from '@ant-design/icons';
import { useNavigate, useParams } from 'react-router-dom';
import AppHeader from '../../components/Header';
import AppFooter from '../../components/Footer';
import { mockPosts } from '../../api/mockData';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import './index.css';

const { Title, Text } = Input;
const { TextArea } = Input;
const { Option } = Select;

const WritePost = ({ loggedInUser, onLogout, onAddPost }) => {
  const [form] = Form.useForm();
  const navigate = useNavigate();
  const { id } = useParams(); // 用于编辑现有文章
  const [loading, setLoading] = useState(false);
  const [isPreview, setIsPreview] = useState(false);
  const [previewContent, setPreviewContent] = useState('');
  const [availableCategories] = useState([
    '前端开发',
    '后端开发', 
    '移动开发',
    '数据库',
    'DevOps',
    '算法',
    '设计模式',
    '项目管理',
    '工具推荐',
    '面试经验'
  ]);
  
  // 仅存储用户手动添加的历史标签，初始值为空数组
  const [userHistoryTags, setUserHistoryTags] = useState([]);

  // 监听标签变化：收集并去重用户手动添加的标签
  const handleTagsChange = (newTags) => {
    // 更新表单中的标签值
    form.setFieldsValue({ tags: newTags });

    // 对用户添加的标签去重，并转换为 { tag: 标签文本 } 的格式
    const uniqueUserTags = Array.from(new Set(newTags)).map((tag) => ({ tag }));

    // 更新用户历史标签
    setUserHistoryTags(uniqueUserTags);
  };

  // （可选）持久化用户历史标签：页面刷新后不丢失
  useEffect(() => {
    // 初始化时，从localStorage读取已保存的历史标签
    const savedHistoryTags = localStorage.getItem('userArticleTags');
    if (savedHistoryTags) {
      setUserHistoryTags(JSON.parse(savedHistoryTags));
    }
  }, []);

  useEffect(() => {
    // 当用户历史标签变化时，同步保存到localStorage
    if (userHistoryTags.length > 0) {
      localStorage.setItem('userArticleTags', JSON.stringify(userHistoryTags));
    } else {
      localStorage.removeItem('userArticleTags');
    }
  }, [userHistoryTags]);

  // 如果是编辑模式，加载现有文章数据
  useEffect(() => {
    if (id) {
      const existingPost = mockPosts.find(post => post.id === id);
      if (existingPost) {
        form.setFieldsValue({
          title: existingPost.title,
          summary: existingPost.summary,
          content: existingPost.content,
          category: existingPost.category,
          tags: existingPost.tags || []
        });
      }
    }
  }, [id, form]);

  // 处理表单提交
  const onFinish = async (values) => {
    if (!loggedInUser) {
      message.error('请先登录！');
      navigate('/auth');
      return;
    }

    setLoading(true);

    try {
      // 生成文章ID
      const newId = Date.now().toString();
      
      // 创建文章对象
      const articleData = {
        id: id || newId, // 如果是编辑，则保留原有ID
        title: values.title,
        summary: values.summary,
        content: values.content,
        author: loggedInUser.name || '匿名用户',
        date: new Date().toISOString().replace('T', ' ').substring(0, 19), // YYYY-MM-DD HH:MM:SS
        views: 0,
        likes: 0,
        category: values.category,
        tags: values.tags || [],
        comments: []
      };

      // 如果是新文章，添加到列表；如果是编辑，更新现有文章
      if (id) {
        // 编辑现有文章的逻辑
        message.success('文章更新成功！');
      } else {
        // 添加新文章的逻辑
        onAddPost(articleData);
        // 同时也要更新 mockPosts 数组，以确保数据一致性
        if (!mockPosts.some(post => post.id === articleData.id)) {
          mockPosts.unshift(articleData);
        }
        message.success('文章发布成功！');
      }

      // 重置表单并跳转到首页
      form.resetFields();
      navigate('/');
    } catch (error) {
      console.error('保存文章失败:', error);
      message.error('保存文章失败，请稍后重试');
    } finally {
      setLoading(false);
    }
  };

  // 预览功能
  const handlePreview = () => {
    const values = form.getFieldsValue();
    if (values.content) {
      setPreviewContent(values.content);
      setIsPreview(true);
    } else {
      message.warning('请先输入文章内容');
    }
  };

  // 取消发布
  const handleCancel = () => {
    Modal.confirm({
      title: '确认取消',
      content: '确定要取消发布吗？未保存的内容将会丢失。',
      okText: '确定',
      cancelText: '取消',
      onOk: () => {
        navigate('/');
      }
    });
  };

  return (
    <Layout style={{ minHeight: '100vh' }}>
      <AppHeader loggedInUser={loggedInUser} onLogout={onLogout} />
      
      <Layout.Content style={{ padding: '20px 50px', marginTop: '64px' }}>
        <div style={{ maxWidth: '800px', margin: '0 auto' }}>
          <Card title={
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span>{id ? '编辑文章' : '撰写新文章'}</span>
              <Space>
                <Button 
                  icon={<EyeOutlined />} 
                  onClick={handlePreview}
                >
                  预览
                </Button>
                <Button 
                  icon={<CloseCircleOutlined />} 
                  onClick={handleCancel}
                >
                  取消
                </Button>
                <Button 
                  type="primary" 
                  icon={<SaveOutlined />} 
                  onClick={() => form.submit()}
                  loading={loading}
                >
                  {id ? '更新文章' : '发布文章'}
                </Button>
              </Space>
            </div>
          }>
            <Form
              form={form}
              layout="vertical"
              onFinish={onFinish}
            >
              {/* 文章标题 */}
              <Form.Item
                label="文章标题"
                name="title"
                rules={[{ required: true, message: '请输入文章标题' }]}
              >
                <Input 
                  placeholder="请输入文章标题" 
                  size="large"
                />
              </Form.Item>

              {/* 文章摘要 */}
              <Form.Item
                label="文章摘要"
                name="summary"
                rules={[{ required: true, message: '请输入文章摘要' }]}
              >
                <TextArea
                  rows={3}
                  placeholder="请输入文章摘要，简要概括文章内容"
                  maxLength={200}
                  showCount
                />
              </Form.Item>

              {/* 文章分类 */}
              <Form.Item
                label="文章分类"
                name="category"
                rules={[{ required: true, message: '请选择文章分类' }]}
              >
                <Select
                  placeholder="请选择文章分类"
                  showSearch
                  filterOption={(input, option) =>
                    option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                  }
                >
                  {availableCategories.map(category => (
                    <Option key={category} value={category}>
                      {category}
                    </Option>
                  ))}
                </Select>
              </Form.Item>

              {/* 文章标签 */}
              <Form.Item
                label="标签"
                name="tags"
              >
                <Select
                  mode="tags"
                  style={{ width: '100%' }}
                  placeholder="选择或输入标签 (按回车键创建新标签)"
                  onChange={handleTagsChange}
                >
                  {userHistoryTags.map((item) => (
                    <Option key={item.tag} value={item.tag}>
                      <Tag color="blue">{item.tag}</Tag>
                    </Option>
                  ))}
                </Select>
              </Form.Item>

              {/* 文章内容 */}
              <Form.Item
                label="文章内容 (支持 Markdown 语法)"
                name="content"
                rules={[{ required: true, message: '请输入文章内容' }]}
              >
                <TextArea
                  rows={15}
                  placeholder={`# 标题\n\n## 二级标题\n\n**粗体文字**\n\n*斜体文字*\n\n- 列表项1\n- 列表项2\n\n[链接文字](http://example.com)\n\n\`\`\`\n// 代码块\nconst hello = 'world';\n\`\`\`\n\n> 引用文字`}
                />
              </Form.Item>

              {/* 提示信息 */}
              <div style={{ padding: '10px', backgroundColor: '#f9f9f9', borderRadius: '4px', marginBottom: '20px' }}>
                <h4>Markdown 语法提示:</h4>
                <ul style={{ margin: 0, paddingLeft: '20px' }}>
                  <li><strong># 标题</strong> - 创建标题</li>
                  <li><strong>**粗体**</strong> - 创建粗体文字</li>
                  <li><strong>*斜体*</strong> - 创建斜体文字</li>
                  <li><strong>- 列表项</strong> - 创建无序列表</li>
    <li><strong>`代码`</strong> - 行内代码</li>
    <li><strong>```代码块```</strong> - 代码块</li>
    <li><strong>&gt; 引用</strong> - 引用文字</li>
  </ul>
</div>
            </Form>
          </Card>
        </div>
      </Layout.Content>

      {/* 预览模态框 */}
      <Modal
        title="文章预览"
        open={isPreview}
        onCancel={() => setIsPreview(false)}
        footer={[
          <Button key="back" onClick={() => setIsPreview(false)}>
            关闭
          </Button>
        ]}
        width={800}
      >
        <div style={{ maxHeight: '60vh', overflowY: 'auto' }}>
          <ReactMarkdown 
            remarkPlugins={[remarkGfm]} 
            children={previewContent}
          />
        </div>
      </Modal>

      <AppFooter />
    </Layout>
  );
};

export default WritePost;